'use client'

import { useProductCardContext } from '@/components/common/product-card/context/product-card-context'
import { ProductCardSizeItem } from '@/components/common/product-card/product-item-sizes/size-item'

import {
  ResponsiveModal,
  ResponsiveModalContent,
  ResponsiveModalDescription,
  ResponsiveModalTitle,
  ResponsiveModalTrigger,
} from '@/components/extension/responsive-modal'
import { Button } from '@/components/ui/button'
import { sortSizesFromObjects } from '@/lib/helpers/sort-size.helper'
import { cn } from '@/lib/utils'
import { VariantMapped } from '@/types/product'
import { Plus } from 'lucide-react'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { useMemo } from 'react'

interface ProductAddMobileProps {
  className?: string
}

export const ProductAddMobile = ({ className }: ProductAddMobileProps) => {
  const { selectedColorVariant, handleAddToCart } = useProductCardContext()
  const t = useTranslations('product_detail.button')

  const sizeOptions = useMemo(
    () =>
      sortSizesFromObjects<VariantMapped>(
        selectedColorVariant?.variants || [],
        'size'
      ),
    [selectedColorVariant]
  )

  const isOnlyOneSize = sizeOptions?.length === 1 && !sizeOptions[0].size

  const handleAddToCartEvent = (variant: VariantMapped) => {
    handleAddToCart(variant)
  }

  return (
    <ResponsiveModal>
      {isOnlyOneSize ? (
        <button
          onClick={() => handleAddToCartEvent(sizeOptions[0])}
          className={cn(
            'absolute right-1 z-10 h-9 w-9 translate-y-3 cursor-pointer rounded-full bg-light/30 p-2.5 backdrop-blur-[17px] transition-all duration-300 lg:hidden',
            className
          )}
        >
          <Image
            src={'/icons/icon-add-cart.svg'}
            alt='icon add cart'
            width={16}
            height={16}
            className='h-4 w-4'
          />
        </button>
      ) : (
        <ResponsiveModalTrigger asChild className='lg:hidden'>
          <button
            className={cn(
              'absolute right-1 z-10 h-9 w-9 translate-y-3 cursor-pointer rounded-full bg-light/30 p-2.5 backdrop-blur-[17px] transition-all duration-300',
              className
            )}
          >
            <Image
              src={'/icons/icon-add-cart.svg'}
              alt='icon add cart'
              width={16}
              height={16}
              className='h-4 w-4'
            />
          </button>
        </ResponsiveModalTrigger>
      )}
      <ResponsiveModalContent className='z-[102] min-h-[214px] gap-0 border-none px-7 py-10'>
        <ResponsiveModalTitle />
        <ResponsiveModalDescription />
        {!isOnlyOneSize ? (
          <>
            <div className='flex items-center gap-1 rounded-t-2xl font-criteria text-sm font-medium leading-4.5 text-neutral-800'>
              {t('quick_add_to_cart')}
              <Plus size={16} className='min-h-4 min-w-4' />
            </div>
            <p className='mt-0.5 font-sans text-xs leading-3.5 text-neutral-400'>
              {t('choose_your_size')}
            </p>
            <ul className='mt-4 flex flex-wrap items-center gap-1'>
              {sizeOptions?.map((variant) => (
                <ProductCardSizeItem
                  title={variant.size}
                  key={variant.id}
                  isDisable={variant.isDisable}
                  onClick={() => handleAddToCartEvent(variant)}
                  id={variant.id}
                  className={cn(variant.size === 'Freesize' && 'w-24')}
                />
              ))}
            </ul>
          </>
        ) : (
          <Button
            variant='black'
            className='flex w-full items-center justify-center gap-1'
            onClick={() =>
              handleAddToCartEvent(selectedColorVariant.variants[0])
            }
          >
            {t('add_cart')} <Plus size={16} className='min-h-4 min-w-4' />
          </Button>
        )}
      </ResponsiveModalContent>
    </ResponsiveModal>
  )
}
